/**
 * Data-table references: {{dt.create_user.accountName}} and {{dt.create user.accountName}}.
 * Request names match case-insensitively; spaces and underscores are the same.
 * Column keys stay exact. These values are not stored as collection variables.
 */

export interface DataTableCatalogEntry {
  requestId: number;
  requestName: string;
  collectionId: number;
  /** Default row (row 0) used when no execution row is active. */
  columns: Record<string, string>;
  /** Data-set rows in execution order. Index matches the row being run. */
  rowColumns?: Record<string, string>[];
}

export interface DataTableCatalogFocus {
  collectionId: number | null;
  parentByCollectionId: Record<number, number | null>;
}

/**
 * `{{name}}` including a request path that contains `{param}` or `{{param}}`.
 * A request named `get/sales/task{{id}}` is written `{{dt.get/sales/task{{id}}.name}}`.
 * The inner `{{id}}` stays inside that data-table token. Plain `{{variable}}` still ends at the first `}}`.
 * `[^}]+` would stop at `{contactId}` and drop the rest of the request name.
 */
export const MUSTACHE_TOKEN_SOURCE =
  '\\{\\{\\s*((?:[dD][tT]\\.(?:[^{}]|\\{[^{}]*\\}|\\{\\{[^{}]*\\}\\})+)|(?:(?:[^{}]|\\{[^{}]*\\})+?))\\s*\\}\\}';

export function mustacheTokenRe(): RegExp {
  return new RegExp(MUSTACHE_TOKEN_SOURCE, 'g');
}

/** Hide {{...}} so a later `{param}` pass does not rewrite path params inside a dt reference. */
export function protectMustacheTokens(input: string): { text: string; restore: (value: string) => string } {
  const tokens: string[] = [];
  const text = String(input ?? '').replace(mustacheTokenRe(), (match) => {
    const id = tokens.length;
    tokens.push(match);
    return `\uE000${id}\uE001`;
  });
  return {
    text,
    restore: (value: string) =>
      value.replace(/\uE000(\d+)\uE001/g, (mark, index) => tokens[Number(index)] ?? mark),
  };
}

/** "Create User", "create user", and "create_user" share this key. */
export function normalizeDataTableRequestKey(name: string): string {
  return String(name ?? '')
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, ' ')
    .trim();
}

/**
 * Parse the inside of {{ }}. The last segment is the column key.
 * Returns null when the token is not a dt.request.column reference.
 */
/** True when text may need the data-table catalog ({{dt.request.column}}). */
export function textUsesDataTableReferences(...parts: (string | undefined | null | unknown)[]): boolean {
  for (const part of parts) {
    if (typeof part === 'string' && /\{\{\s*dt\./i.test(part)) {
      return true;
    }
  }
  return false;
}

export function parseDataTableReference(token: string): { requestKey: string; column: string } | null {
  const raw = String(token ?? '').trim();
  const match = /^dt\.(.+)$/i.exec(raw);
  if (!match) return null;
  const rest = match[1].trim();
  const dot = rest.lastIndexOf('.');
  if (dot <= 0 || dot >= rest.length - 1) return null;
  const requestPart = rest.slice(0, dot).trim();
  const column = rest.slice(dot + 1).trim();
  if (!requestPart || !column || /\s/.test(column)) return null;
  return { requestKey: normalizeDataTableRequestKey(requestPart), column };
}

export function parentByCollectionIdFromList(
  collections: Array<{ id?: number | null; parentCollectionId?: number | null }>
): Record<number, number | null> {
  const map: Record<number, number | null> = {};
  for (const collection of collections ?? []) {
    const id = Number(collection?.id);
    if (!Number.isFinite(id) || id <= 0) continue;
    const parent = collection.parentCollectionId;
    map[id] = parent != null && Number.isFinite(Number(parent)) && Number(parent) > 0 ? Number(parent) : null;
  }
  return map;
}

/** Focus collection, its parents, and its nested collections. */
export function collectionTreeIds(
  focusCollectionId: number,
  parentByCollectionId: Record<number, number | null>
): number[] {
  const ids = new Set<number>();
  if (!Number.isFinite(focusCollectionId) || focusCollectionId <= 0) return [];
  ids.add(focusCollectionId);
  let cursor: number | null = focusCollectionId;
  const seen = new Set<number>();
  while (cursor != null && !seen.has(cursor)) {
    seen.add(cursor);
    const parent: number | null | undefined = parentByCollectionId[cursor];
    if (parent == null) break;
    ids.add(parent);
    cursor = parent;
  }
  let grew = true;
  while (grew) {
    grew = false;
    for (const [idRaw, parent] of Object.entries(parentByCollectionId)) {
      const id = Number(idRaw);
      if (parent != null && ids.has(parent) && !ids.has(id)) {
        ids.add(id);
        grew = true;
      }
    }
  }
  return Array.from(ids);
}

/**
 * Steps between two collections in one tree: same folder is 0, parent or child is 1,
 * sibling folders (Postman `summary` and `{id}`) are 2. Unconnected ids stay unreachable.
 */
function collectionDistance(
  fromId: number,
  toId: number,
  parentByCollectionId: Record<number, number | null>
): number | null {
  if (!Number.isFinite(fromId) || !Number.isFinite(toId)) return null;
  if (fromId === toId) return 0;

  const children = new Map<number, number[]>();
  const parentOf = new Map<number, number>();
  for (const [idRaw, parent] of Object.entries(parentByCollectionId ?? {})) {
    const id = Number(idRaw);
    if (!Number.isFinite(id) || parent == null) continue;
    const parentId = Number(parent);
    if (!Number.isFinite(parentId)) continue;
    parentOf.set(id, parentId);
    const list = children.get(parentId) ?? [];
    list.push(id);
    children.set(parentId, list);
  }

  const queue: Array<{ id: number; dist: number }> = [{ id: fromId, dist: 0 }];
  const seen = new Set<number>();
  while (queue.length) {
    const next = queue.shift();
    if (!next || seen.has(next.id)) continue;
    seen.add(next.id);
    if (next.id === toId) return next.dist;
    const parent = parentOf.get(next.id);
    if (parent != null && !seen.has(parent)) {
      queue.push({ id: parent, dist: next.dist + 1 });
    }
    for (const child of children.get(next.id) ?? []) {
      if (!seen.has(child)) queue.push({ id: child, dist: next.dist + 1 });
    }
  }
  return null;
}

/**
 * Resolve one dt reference. When more than one request matches at the same
 * distance, returns undefined so the token stays unresolved.
 */
export function resolveDataTableColumn(
  token: string,
  catalog: DataTableCatalogEntry[],
  focus?: DataTableCatalogFocus | null
): string | undefined {
  const parsed = parseDataTableReference(token);
  if (!parsed) return undefined;
  const matches = (catalog ?? []).filter(
    (entry) => normalizeDataTableRequestKey(entry.requestName) === parsed.requestKey
  );
  if (matches.length === 0) return undefined;

  let candidates = matches;
  const focusId = focus?.collectionId != null ? Number(focus.collectionId) : NaN;
  if (Number.isFinite(focusId) && focusId > 0) {
    const parentByCollectionId = focus?.parentByCollectionId ?? {};
    const ranked = matches
      .map((entry) => ({
        entry,
        distance: collectionDistance(focusId, Number(entry.collectionId), parentByCollectionId),
      }))
      .filter((row): row is { entry: DataTableCatalogEntry; distance: number } => row.distance != null);
    if (ranked.length === 0) return undefined;
    const best = Math.min(...ranked.map((row) => row.distance));
    candidates = ranked.filter((row) => row.distance === best).map((row) => row.entry);
  }
  if (candidates.length !== 1) return undefined;
  const columns = columnsForExecutionRow(candidates[0]);
  if (!Object.prototype.hasOwnProperty.call(columns, parsed.column)) return undefined;
  const value = columns[parsed.column];
  return value == null ? '' : String(value);
}

const rememberedCatalog = new Map<number, DataTableCatalogEntry>();
let activeCatalog: DataTableCatalogEntry[] = [];
let activeFocus: DataTableCatalogFocus | null = null;
/** 0-based data-set row currently executing. Null uses each request's first row. */
let activeDataTableRowIndex: number | null = null;

function columnsForExecutionRow(entry: DataTableCatalogEntry): Record<string, string> {
  const index = activeDataTableRowIndex;
  const rows = entry.rowColumns;
  // Once a data-set round is running, never fall back to `columns`.
  // That map keeps the first non-empty cell, so data set 2 and 3 would still read row 1.
  if (index != null && index >= 0) {
    if (rows && index < rows.length) return rows[index] ?? {};
    return {};
  }
  return entry.columns ?? {};
}

/** One column map per data-table row, in table order. Skips internal `__` fields. */
export function tableRowsToDataTableColumns(
  rows: Array<Record<string, any>> | null | undefined
): Record<string, string>[] {
  if (!rows?.length) return [];
  return rows.map((row) => {
    const out: Record<string, string> = {};
    if (!row || typeof row !== 'object') return out;
    for (const [key, value] of Object.entries(row)) {
      if (!key || key.startsWith('__') || value == null || typeof value === 'object') continue;
      out[key] = String(value);
    }
    return out;
  });
}

/** Bind {{dt.request.column}} to this data-set row for the current execution step. */
export function setActiveDataTableRowIndex(index: number | null): void {
  if (index == null || !Number.isFinite(index) || index < 0) {
    activeDataTableRowIndex = null;
    return;
  }
  activeDataTableRowIndex = Math.floor(index);
}

function copyRowMap(row: Record<string, string> | null | undefined): Record<string, string> {
  return { ...(row ?? {}) };
}

/** Incoming cells win. Keys the incoming row does not mention stay from the stored row. */
function overlayRowMap(
  previous: Record<string, string> | null | undefined,
  incoming: Record<string, string> | null | undefined
): Record<string, string> {
  const out = copyRowMap(incoming);
  for (const [key, value] of Object.entries(previous ?? {})) {
    if (!(key in out)) out[key] = value;
  }
  return out;
}

/**
 * A live table snapshot often has only the selected row. Keep the later saved rows so
 * data set 2 and 3 still read their own Amount instead of falling through to row 1.
 */
function mergeRememberedRowColumns(
  previous: Record<string, string>[] | null | undefined,
  incoming: Record<string, string>[] | null | undefined
): Record<string, string>[] | undefined {
  if (!incoming?.length) return previous?.map((row) => copyRowMap(row));
  const incomingRows = incoming.map((row) => copyRowMap(row));
  if (!previous?.length) return incomingRows;
  if (incomingRows.length >= previous.length) {
    return incomingRows.map((row, index) => overlayRowMap(previous[index], row));
  }
  return previous.map((row, index) =>
    index < incomingRows.length ? overlayRowMap(row, incomingRows[index]) : copyRowMap(row)
  );
}

export function rememberDataTableCatalogEntry(entry: DataTableCatalogEntry): void {
  const requestId = Number(entry?.requestId);
  if (!Number.isFinite(requestId) || requestId <= 0) return;
  const previous = rememberedCatalog.get(requestId);
  rememberedCatalog.set(requestId, {
    requestId,
    requestName: String(entry.requestName ?? '') || previous?.requestName || '',
    collectionId: Number(entry.collectionId) || previous?.collectionId || 0,
    columns: overlayRowMap(previous?.columns, entry.columns),
    rowColumns: mergeRememberedRowColumns(previous?.rowColumns, entry.rowColumns),
  });
}

export function rememberDataTableCatalogEntries(entries: DataTableCatalogEntry[]): void {
  for (const entry of entries ?? []) rememberDataTableCatalogEntry(entry);
}

export function getRememberedDataTableCatalog(): DataTableCatalogEntry[] {
  return Array.from(rememberedCatalog.values());
}

export function setActiveDataTableCatalog(
  entries: DataTableCatalogEntry[] | null | undefined,
  focus?: DataTableCatalogFocus | null
): void {
  activeCatalog = Array.isArray(entries) ? entries : [];
  activeFocus = focus ?? null;
}

export function clearActiveDataTableCatalog(): void {
  activeCatalog = [];
  activeFocus = null;
  activeDataTableRowIndex = null;
}

export function clearRememberedDataTableCatalog(): void {
  rememberedCatalog.clear();
}

export function lookupActiveDataTableReference(token: string): string | undefined {
  if (!parseDataTableReference(token)) return undefined;
  if (activeCatalog.length) {
    const fromActive = resolveDataTableColumn(token, activeCatalog, activeFocus);
    if (fromActive !== undefined) return fromActive;
  }
  if (rememberedCatalog.size === 0) return undefined;
  return resolveDataTableColumn(token, getRememberedDataTableCatalog(), activeFocus);
}

export function activateRememberedDataTableCatalog(opts: {
  focusCollectionId: number | null;
  parentByCollectionId: Record<number, number | null>;
  liveEntry?: DataTableCatalogEntry | null;
}): void {
  if (opts.liveEntry) rememberDataTableCatalogEntry(opts.liveEntry);
  const focusId = opts.focusCollectionId != null ? Number(opts.focusCollectionId) : null;
  if (focusId == null || !Number.isFinite(focusId) || focusId <= 0) {
    setActiveDataTableCatalog([], {
      collectionId: null,
      parentByCollectionId: opts.parentByCollectionId ?? {},
    });
    return;
  }
  const tree = new Set(collectionTreeIds(focusId, opts.parentByCollectionId ?? {}));
  const entries = getRememberedDataTableCatalog().filter((entry) => tree.has(Number(entry.collectionId)));
  setActiveDataTableCatalog(entries, {
    collectionId: focusId,
    parentByCollectionId: opts.parentByCollectionId ?? {},
  });
}

/** Token typed after `dt.` — "Latest OTP" and "latest_otp" both become latest-otp. */
export function dataTableRequestSuggestionToken(name: string): string {
  return String(name ?? '')
    .trim()
    .toLowerCase()
    .replace(/[\s_]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-+|-+$/g, '');
}

export interface DataTableSuggestRequest {
  token: string;
  label: string;
  columns: string[];
}

export interface DataTableSuggestQuery {
  kind: 'request' | 'column';
  filter: string;
  requestToken?: string;
  tokenStart: number;
  tokenEnd: number;
}

export interface DataTableSuggestionItem {
  kind: 'request' | 'column';
  label: string;
  /** Request token or column key inserted into `{{dt...}}`. */
  insert: string;
}

function tokenEndIndex(text: string, caret: number): number {
  let i = caret;
  while (i < text.length) {
    if (text.startsWith('}}', i)) return i + 2;
    if (!/[A-Za-z0-9_.{}\/\- ]/.test(text[i])) break;
    i += 1;
  }
  return i;
}

/**
 * While the caret is inside `{{dt.` or `{{dt.request.`, describe which list to show.
 * Returns null for any other `{{` token.
 */
export function queryDataTableSuggestions(text: string, caret: number): DataTableSuggestQuery | null {
  const safeCaret = Math.max(0, Math.min(caret, text.length));
  const upto = text.slice(0, safeCaret);
  const open = upto.lastIndexOf('{{');
  if (open < 0) return null;
  const inner = upto.slice(open + 2);
  if (inner.includes('}}')) return null;
  const body = inner.replace(/^\s+/, '');
  if (!/^dt\./i.test(body)) return null;
  const afterDt = body.slice(3);
  const dot = afterDt.lastIndexOf('.');
  const tokenEnd = tokenEndIndex(text, safeCaret);
  if (dot === -1) {
    return { kind: 'request', filter: afterDt, tokenStart: open, tokenEnd };
  }
  return {
    kind: 'column',
    filter: afterDt.slice(dot + 1),
    requestToken: afterDt.slice(0, dot),
    tokenStart: open,
    tokenEnd,
  };
}

export function filterDataTableSuggestions(
  query: DataTableSuggestQuery,
  requests: DataTableSuggestRequest[]
): DataTableSuggestionItem[] {
  const list = Array.isArray(requests) ? requests : [];
  if (query.kind === 'request') {
    const filterKey = normalizeDataTableRequestKey(query.filter);
    const seen = new Set<string>();
    const items: DataTableSuggestionItem[] = [];
    for (const request of list) {
      const token = String(request.token ?? '').trim();
      if (!token || seen.has(token)) continue;
      const tokenKey = normalizeDataTableRequestKey(token);
      const labelKey = normalizeDataTableRequestKey(request.label || token);
      if (filterKey && !tokenKey.startsWith(filterKey) && !labelKey.startsWith(filterKey)) continue;
      seen.add(token);
      items.push({ kind: 'request', label: request.label || token, insert: token });
    }
    return items;
  }
  const requestKey = normalizeDataTableRequestKey(query.requestToken || '');
  if (!requestKey) return [];
  const filter = String(query.filter ?? '').toLowerCase();
  const columns: DataTableSuggestionItem[] = [];
  const seen = new Set<string>();
  for (const request of list) {
    if (normalizeDataTableRequestKey(request.token) !== requestKey && normalizeDataTableRequestKey(request.label) !== requestKey) {
      continue;
    }
    for (const column of request.columns ?? []) {
      const name = String(column ?? '').trim();
      if (!name || name.startsWith('__') || seen.has(name)) continue;
      if (filter && !name.toLowerCase().startsWith(filter)) continue;
      seen.add(name);
      columns.push({ kind: 'column', label: name, insert: name });
    }
  }
  return columns;
}

export function applyDataTableReferences(text: string): string {
  if (!text || !text.includes('{{')) return text;
  let result = text;
  for (let depth = 0; depth < 8; depth += 1) {
    if (!result.includes('{{')) break;
    const next = result.replace(mustacheTokenRe(), (match, rawName) => {
      const value = lookupActiveDataTableReference(String(rawName ?? '').trim());
      return value !== undefined ? value : match;
    });
    if (next === result) break;
    result = next;
  }
  return result;
}
